<template>
  <div
    class="info-block"
    :class="[typeClass, state.type === 'default' && 'is--full-detail-width-small']"
  >
    <div v-if="state.type === 'info' && state.label" class="info-block-label">
      <Label type="info" title="Info" />
    </div>
    <div v-if="state.title" class="info-block-title">{{ state.title }}</div>

    <Alinea
      v-if="state.text && state.text.blocks"
      variant="rte"
      :blocks="state.text.blocks"
      class="info-block-text"
      :class="{ 'wcag-links': state.type === 'default' }"
    />

    <div v-if="state.type === 'default' && state.button" class="info-block-button">
      <Button
        :label="state.button.label"
        :icon="
          state.button.target === '_blank'
            ? 'eenvandaag-general:external-2'
            : 'eenvandaag-general:arrow-right'
        "
        :to="state.button.to"
        :target="state.button.target"
        type="text-wcag"
        icon-position="right"
        :analytics="{ linkAnalyticsTag: 'info-block' }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import type { IButton, TInfoBlock } from '~~/types/local_types';

const props = defineProps({
  type: {
    type: String as () => TInfoBlock,
    default: 'default',
  },
  label: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  text: {
    type: Object as () => { blocks: Array<never> },
    default: null,
  },
  button: {
    type: Object as () => IButton,
    default: () => {},
  },
});

const state = reactive({
  type: props.type,
  title: props.title,
  text: props.text,
  label: props.type === 'info' ? 'INFO' : props.label,
  button: props.button,
});

const typeClass = `is--${props.type}`;
</script>

<style lang="postcss" scoped>
.info-block {
  --info-block-margin: var(--unit-8) 0;
  --info-block-width: 100%;
  --info-block-gap: 9px;
  --info-block-padding: var(--unit-16) var(--unit-18) var(--unit-20) var(--unit-18);
  --info-block-border-width: 3px;
  --info-block-border: var(--info-block-border-width) solid hsl(var(--color-secondary-500));
  --info-block-background: hsl(var(--color-primary-50));

  display: flex;
  width: var(--info-block-width);
  margin: var(--info-block-margin);
  flex-direction: column;
  align-items: flex-start;
  padding: var(--info-block-padding);
  gap: var(--info-block-gap);

  border-radius: 0;
  border-left: var(--info-block-border);
  background: var(--info-block-background);

  &.is--info {
    --info-block-gap: var(--unit-12);
    --info-block-border: var(--info-block-border-width) solid hsl(var(--color-primary-700));
  }

  @media (--lg-viewport) {
    --info-block-border-width: 4px;
    --info-block-gap: 12px;
    --info-block-padding: var(--unit-24) var(--unit-32) var(--unit-32) var(--unit-32);
    --info-block-border: 4px solid hsl(var(--color-secondary-500));
  }
}

.info-block-title {
  color: hsl(var(--color-primary-900));
  word-break: break-word;

  @add-mixin heading-lg;
}

.info-block-button {
  --info-block-button-padding: 9px 0;

  padding: var(--info-block-button-padding);
  align-self: flex-start;

  @media (--lg-viewport) {
    --info-block-button-padding: var(--unit-12) 0;
  }
}

/* the link for the grey variant of this component needs a different link (higher contrast)*/
.wcag-links {
  --button-color: hsl(var(--color-secondary-700)) !important;

  :deep(a[href]) {
    &::before {
      background-color: hsl(var(--color-secondary-700));
    }

    color: hsl(var(--color-secondary-700));

    @add-mixin hover {
      color: hsl(var(--color-secondary-500));
    }
  }
}
</style>
